'use client';

import { Button, Sheet, SheetContent, SheetDescription, SheetTitle } from '@shared/components/shadcn';
import { NumberFormats, useClientConfig, useCurrencyFormatter, useIsMobile, type Currency } from '@shared/utils';
import { useTranslations } from 'next-intl';

type CancelAutoRenewalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  totalWinnings?: number;
  startDate: Date;
  lastDrawDate: Date;
  isLoading?: boolean;
};

const winningsMessageThreshold: Partial<Record<Currency, number>> = {
  GBP: 10000, // £100.00
  AUD: 20000, // A$200.00
};

function CancelAutoRenewalModal({ isOpen, onClose, onConfirm, totalWinnings, startDate, lastDrawDate, isLoading = false }: CancelAutoRenewalModalProps) {
  const t = useTranslations('lotteries.ticket_details.cancel_auto_renewal_modal');
  const formatCurrency = useCurrencyFormatter();
  const isMobile = useIsMobile();
  const { currency } = useClientConfig();

  const hasSignificantWinnings = totalWinnings !== undefined && totalWinnings > (winningsMessageThreshold[currency] ?? 0);
  const formattedWinnings = hasSignificantWinnings ? formatCurrency(totalWinnings).format(NumberFormats.WITH_DECIMALS).get() : '';

  const winningsMessage = hasSignificantWinnings ? t('winnings_message', { amount: formattedWinnings, startDate }) : t('no_significant_winnings_message', { date: lastDrawDate });

  const confirmLabel = isLoading ? t('cancelling_button') : t('confirm_button');
  const preventClose = (e: Event) => e.preventDefault();

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={isMobile ? 'h-auto pb-8' : ''}
        onPointerDownOutside={preventClose}
        onEscapeKeyDown={preventClose}
        data-track-container="Cancel Auto Renew"
        data-track-type={hasSignificantWinnings ? 'Significant Win' : 'No Significant Win'}
        data-testid="cancel-auto-renewal-modal"
        title={t('title')}
      >
        <SheetDescription className="sr-only" data-testid="modal-description-aria">
          {t('confirmation_question')}
        </SheetDescription>
        <div className={`flex items-center  px-8 py-6 border-b border-border min-h-[40px] ${!isMobile ? 'justify-center' : ''}`}>
          <SheetTitle className="paragraph-explanatory-strong" data-testid="modal-title">
            {t('title')}
          </SheetTitle>
        </div>
        <div className="px-8 py-8 space-y-2">
          <h5 className="mb-2" data-testid="modal-description">
            {t('confirmation_question')}
          </h5>
          {winningsMessage && (
            <p className="paragraph-explanatory" data-testid="modal-winnings-info">
              {winningsMessage}
            </p>
          )}
        </div>
        <div className="px-8 flex flex-col gap-4" data-track-container="Actions">
          <Button className="w-full" onClick={onConfirm} disabled={isLoading} data-track-name="Yes" data-testid="confirm-button">
            {confirmLabel}
          </Button>
          <Button className="w-full" variant="secondary" onClick={onClose} disabled={isLoading} data-track-name="No" data-testid="cancel-button">
            {t('cancel_button')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { CancelAutoRenewalModal };
